import { useEffect, useState, useCallback } from "react";
import { gql, useMutation } from "@apollo/client";
import { useHistory, useLocation } from "react-router-dom";
import { useAnalytics, Events, useQueryParams } from "@hooks";
import { getOrCreateSessionId, setSessionUserId } from "../utils/sentryTrackingConfig";
import { setLogoutEventsSent } from "../utils/logoutTrackingGuard";
import { LoginResponse } from "./useLoginMutation";
import { useAuthToken } from "./useAuthToken";
import { isAlphaOrProd } from "@src/constants";
import { logger } from '../utils/logger';

const CleverSSOAuthMutationGQL = gql`
  mutation cleverSSOLogin($input: CleverLoginInput!) {
    cleverSSOLogin(input: $input) {
      token
      uri
      sid
      verified
      email
      id
    }
  }
`;

const CleverSSOUserMutationGQL = gql`
  mutation getCleverUser($input: CleverLoginInput!) {
    getCleverUser(input: $input) {
      firstName
      lastName
      email
      accessToken
      id
    }
  }
`;

export type CleverUserResponse = {
  email: string;
  firstName: string;
  lastName: string;
  accessToken: string;
  id?: string;
};

const cleverConfig = {
  clientId: import.meta.env.VITE_CLEVER_CLIENT_ID || "8374a7ed324a2d169e1f",
  districtId: "662ff80eca2429850631906f",
  redirectUri: `${window.origin}/login/`,
};

export default function useCleverAuth(type: "Login" | "Registration") {
  const [cleverAuthLoading, setCleverAuthLoading] = useState(false);
  const [cleverToken, setCleverToken] = useState("");
  const { setAuthToken, removeAuthToken } = useAuthToken();
  const { push } = useHistory();
  const params = useQueryParams();
  const returnUrl = params.get("returnUrl");
  const { search } = useLocation();
  const [cleverUser, setCleverUser] = useState<{
    accessToken: string;
    email: string;
    firstName: string;
    lastName: string;
  }>();

  const { trackEvent, segment, identify } = useAnalytics();
  const [mutation, { error }] = useMutation<{ cleverSSOLogin: LoginResponse }>(
    CleverSSOAuthMutationGQL,
    {
      onCompleted: ({ cleverSSOLogin }) => {
        if (cleverSSOLogin.token) {
          setLogoutEventsSent(false);
          removeAuthToken();
          setAuthToken(cleverSSOLogin.token, true);
          if (cleverSSOLogin.id) {
            segment?.identify(cleverSSOLogin.id);
            identify(cleverSSOLogin.id, {
              email: cleverSSOLogin.email,
            });
          }
          const sessionId = getOrCreateSessionId();
          const timestamp = new Date().toISOString();
          const d = { email: cleverSSOLogin.email, loginType: "Clever", userId: cleverSSOLogin.id, sessionId, timestamp };
          trackEvent(Events.actions.logIn, d);
          trackEvent(Events.actions.sessionStarted, d);
          if (cleverSSOLogin.id) setSessionUserId(cleverSSOLogin.id);
          if (
            returnUrl &&
            search.replace("?returnUrl=", "").includes(returnUrl)
          ) {
            const sharePath = search
              .replace("?returnUrl=", "")
              .replace(`${returnUrl}&`, `${returnUrl}?`);
            push(sharePath);
          } else {
            push("/");
          }
        } else if (cleverSSOLogin.sid) {
          const state: any = {
            totpId: cleverSSOLogin.sid,
            email: cleverSSOLogin.email,
          };
          if (cleverSSOLogin.verified !== "true" && cleverSSOLogin.uri) {
            state.totpUri = cleverSSOLogin.uri;
          }
          push("/verify", state);
        } else if (cleverSSOLogin.email) {
          push("/admin/verify", { email: cleverSSOLogin.email });
        }
      },
      onError: (error, options) => {
        trackEvent(Events.actions.logInFailed, {
          email: options?.variables?.email,
          loginType: "Clever",
          clientId: options?.variables?.clientId,
          message: "Login attempt failed",
          error: error.message,
        });
        logger.debug(`[Error]`, error);
      },
    }
  );
  const [getCleverUserMutation, { error: cleverUserError }] = useMutation<{
    getCleverUser: CleverUserResponse;
  }>(CleverSSOUserMutationGQL, {
    onCompleted: ({ getCleverUser }) => {
      if (getCleverUser.email) {
        setCleverUser(getCleverUser);
      }
    },
    onError: (error, options) => {
      logger.debug(`[Error]`, error);
    },
  });

  const login = useCallback(
    async (code, redirect_uri) => {
      if (!code || !redirect_uri) return;
      try {
        if (type === "Login") {
          await mutation({
            variables: {
              input: {
                code,
                redirect_uri,
              },
            },
          });
        } else {
          await getCleverUserMutation({
            variables: {
              input: {
                code,
                redirect_uri,
              },
            },
          });
        }
      } catch (error) {
        logger.error("Login failed:", error);
      } finally {
        setCleverAuthLoading(false);
      }
    },
    [mutation, getCleverUserMutation, type]
  );

  const cleverLogin = () => {
    setCleverAuthLoading(true);
    const url = isAlphaOrProd
      ? `https://clever.com/oauth/authorize?response_type=code&redirect_uri=${cleverConfig.redirectUri}&client_id=${cleverConfig.clientId}`
      : `https://clever.com/oauth/authorize?response_type=code&redirect_uri=${cleverConfig.redirectUri}&client_id=${cleverConfig.clientId}&district_id=${cleverConfig.districtId}`;

    function popupWindow({ url, title, win, w, h }) {
      const y = win.top.outerHeight / 2 + win.top.screenY - h / 2;
      const x = win.top.outerWidth / 2 + win.top.screenX - w / 2;
      return win.open(
        url,
        title,
        `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width=${w}, height=${h}, top=${y}, left=${x}`
      );
    }
    const popup = popupWindow({
      url,
      title: "Clever SSO",
      win: window,
      w: 483,
      h: 599,
    });
    const checkPopup = setInterval(() => {
      try {
        const code = popup?.window.location?.search.replace("?code=", "");
        if (
          popup?.window?.location?.href.includes(cleverConfig.redirectUri) &&
          code
        ) {
          setCleverToken(code);
          popup.close();
        }
        if (popup.closed) {
          clearInterval(checkPopup);
        }
        if (!popup || !popup.closed) return;
        clearInterval(checkPopup);
      } catch (err) {
        if (popup.closed && !cleverToken) {
          setCleverAuthLoading(false);
        }
        if (popup.closed) {
          clearInterval(checkPopup);
        }
      }
    }, 100);
  };

  useEffect(() => {
    if (cleverToken) {
      login(cleverToken, cleverConfig.redirectUri);
    }
  }, [cleverToken, login]);

  return {
    cleverAuth: cleverLogin,
    cleverAuthLoading,
    cleverUser,
    setCleverUser,
    cleverError: error || cleverUserError,
  };
}
